import React, { useRef, useState, useEffect } from 'react';
import { Box, Stack, Collapse, Typography, TextareaAutosize } from '@mui/material';
import CharacterCounter from './CharacterCounter';
import BasicButton from '../button/BasicButton';
import CustomTextareaAutosize from './CustomTextareaAutosize';
import theme from '../../theme';

const CommentReplyForm = ({
  isOpen,
  replyContent,
  setReplyContent,
  onSubmit,
  onCancel,
  maxLength = 5000,
  placeholder = '댓글을 작성해 주세요.',
  submitText = '등록',
  cancelText = '취소',
  mentionedNickname = null,
  mt = 1.5,
  mb = 1.5,
}) => {
  const textareaRef = useRef(null);
  const mentionRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);
  const [mentionWidth, setMentionWidth] = useState(0);

  useEffect(() => {
    if (mentionRef.current) {
      setMentionWidth(mentionRef.current.offsetWidth + 4);
    }
  }, [mentionedNickname, isOpen]);

  const handleTextareaChange = (e) => {
    if (maxLength && e.target.value.length > maxLength) return;
    setReplyContent(e.target.value);
  };

  return (
    <Collapse in={isOpen} timeout={0}>
      <Box component="form" onSubmit={onSubmit} mt={mt} mb={mb} data-testid="reply-form">
        <Stack spacing={1}>
          {mentionedNickname ? (
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                border: `${isFocused ? '2px' : '1px'} solid ${isFocused ? theme.palette.black[9] : theme.palette.grey.line}`,
                borderRadius: '4px',
                boxSizing: 'border-box',
                cursor: 'text',
                backgroundColor: theme.palette.surface.main,
              }}
              onClick={() => textareaRef.current?.focus()}
            >
              <Typography
                ref={mentionRef}
                component="span"
                fontSize={14}
                fontWeight="bold"
                color={theme.palette.black[9]}
                sx={{
                  position: 'absolute',
                  top: '16px',
                  left: '12px',
                  whiteSpace: 'nowrap',
                  lineHeight: 1.5,
                  userSelect: 'none',
                  pointerEvents: 'none',
                }}
              >
                @{mentionedNickname}
              </Typography>
              <TextareaAutosize
                ref={textareaRef}
                minRows={3}
                value={replyContent}
                onChange={handleTextareaChange}
                maxLength={maxLength}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                style={{
                  border: 'none',
                  outline: 'none',
                  resize: 'vertical',
                  width: '100%',
                  boxSizing: 'border-box',
                  fontSize: '14px',
                  lineHeight: 1.5,
                  fontFamily: 'inherit',
                  padding: '16px 12px',
                  textIndent: `${mentionWidth}px`,
                  color: 'var(--pp-text-primary)',
                  background: 'transparent',
                }}
              />
            </Box>
          ) : (
            <CustomTextareaAutosize
              minRows={3}
              placeholder={placeholder}
              value={replyContent}
              onChange={(e) => setReplyContent(e.target.value)}
              maxLength={maxLength}
              sx={{ background: theme.palette.surface.main }}
            />
          )}
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
          >
            <Box
              sx={{
                width: { xs: '100%', md: 'auto' },
                display: 'flex',
                justifyContent: { xs: 'flex-end', md: 'flex-start' },
              }}
            >
              <CharacterCounter currentLength={replyContent?.length || 0} maxLength={maxLength} />
            </Box>
            <Box display="flex" gap={1} width={{ xs: '100%', md: 'auto' }}>
              <BasicButton
                variant="outlined"
                onClick={onCancel}
                sx={{
                  width: { xs: '100%', md: 'auto' },
                  minWidth: { md: '70px', lg: '58px' },
                  height: { xs: '40px', md: '40px', lg: '30px' },
                  padding: '8px 16px',
                  typography: 'body3',
                  fontWeight: 'medium',
                }}
              >
                {cancelText}
              </BasicButton>
              <BasicButton
                variant="contained"
                type="submit"
                disabled={!replyContent.trim()}
                sx={{
                  width: { xs: '100%', md: 'auto' },
                  minWidth: { md: '70px', lg: '58px' },
                  height: { xs: '40px', md: '40px', lg: '30px' },
                  padding: '8px 16px',
                  typography: 'body3',
                  fontWeight: 'medium',
                  color: theme.palette.primary.contrastText,
                }}
              >
                {submitText}
              </BasicButton>
            </Box>
          </Stack>
        </Stack>
      </Box>
    </Collapse>
  );
};

export default CommentReplyForm;
